import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps } from 'src/Library/Types';
import { OrganisationOnboardingStatus,
  StatusTransitionAction } from 'src/components/pages/masters/Masters.Helper';
import { commonStyles, buttonStyle } from 'src/Library/CommonStyles';
import ViewKYCModal from '../masters/ViewKYCModal';
import { useGenericState } from 'src/Library/UseGenericState';
import Button from 'antd/lib/button';
import ConfirmConnectionRequestModal from '../masters/ConfirmConnectionRequestModal';
import { Icon, Tooltip } from 'antd';
import { tokens } from 'src/design-system';

const styles = (theme) => ({
  connectionRequest: {
    color: tokens.colors.textSecondary,
    marginTop: '5%',
  },
  pendingApproval: {
    marginTop: '8px',
    ...commonStyles(theme).justifySpaceBetween,
  },
  acceptRejectOptions: {
    fontSize: `${tokens.fontSize.small}px`,
  },
  rejectButton: {
    marginRight: '16px',
    color: '#F5222D',
    cursor: 'pointer',
    fontWeight: tokens.fontWeight.semibold,
  },
  acceptButton: {
    ...buttonStyle(tokens.colors.bgWhite, '#00BC79', '1px solid #00BC79'),
    fontSize: `${tokens.fontSize.small}px`,
    borderRadius: `${tokens.radius.sm}px`,
  },
  viewKYC: {
    color: tokens.colors.primary,
    fontSize: `${tokens.fontSize.small}px`,
    cursor: 'pointer',
  },
  rejected: {
    backgroundColor: '#FFF8F8',
    border: '1px solid #FFD6D6',
    color: '#E0000C',
    fontSize: `${tokens.fontSize.small}px`,
    padding: '3px 5px',
    borderRadius: `${tokens.radius.md}px`,
  },
  connectionStatusMessage: {
    fontStyle: 'italic',
    fontSize: `${tokens.fontSize.small}px`,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  connectionStatus: {
    fontSize: `${tokens.fontSize.small}px`,
    fontStyle: 'italic',
    color: '#666666',
    marginBottom: '4px',
  },
  rejectionStatus: {
    marginTop: '8px',
    ...commonStyles(theme).flexColumn,
    alignItems: 'flex-end',
  },
  rejectionRemark: {
    color: '#ECAF36',
    fontSize: `${tokens.fontSize.h3}px`,
    marginRight: '6px',
  },
});

interface ConnectionStatusInquiryProps extends
  StylesProps<ReturnType<typeof styles>> {
    inquiryData: any;
    handleReload: () => void;
}

interface StateProps {
  showViewKycModal: boolean;
  showConfirmConnectionModal: boolean;
  confirmConnectionDetails: any;
}

const ConnectionStatusInquiry = (props: ConnectionStatusInquiryProps): React.ReactElement => {

  const {
    classes,
    inquiryData,
    handleReload,
  } = props;

  const [state, setState] = useGenericState<StateProps>({
    showViewKycModal: false,
    showConfirmConnectionModal: false,
    confirmConnectionDetails: undefined,
  });

  const {
    showViewKycModal,
    showConfirmConnectionModal,
    confirmConnectionDetails,
  } = state;

  const renderViewKYCModal = () => {
    const { onboardingId } = inquiryData;
    return (
      <ViewKYCModal
      visible={showViewKycModal}
      onClose={() => {
        setState({
          showViewKycModal: false,
        })
      }}
      onboardingId={onboardingId}
    />
    );
  }

  const toggleConnectionConfirmationModal = (statusTransition) => {
    const { onboardingId, shipperName, organisationLogoUrl, lastStatusUpdatedAt } = inquiryData;
    setState({
      showConfirmConnectionModal: true,
      confirmConnectionDetails: {
        statusTransition,
        onboardingId,
        shipperName,
        organisationLogoUrl,
        lastStatusUpdatedAt,
      }
    });
  }
    
  const renderAcceptAndReject = () => {
    return (
      <div className={classes.acceptRejectOptions}>
        <span className={classes.rejectButton}
          onClick={() => {
            toggleConnectionConfirmationModal(StatusTransitionAction.REJECT)
          }}>Reject</span>
        <Button size="small" className={classes.acceptButton}
          onClick={() => {
            toggleConnectionConfirmationModal(StatusTransitionAction.APPROVE)
          }}>Accept</Button>
      </div>
    );
  };

  const renderPendingStatus = () => {
    return (
      <React.Fragment>
        <span className={classes.connectionStatusMessage}>
          To process booking, accept the connection request first.
        </span>
        <span className={classes.pendingApproval}>
          <span className={classes.viewKYC}
            onClick={() => setState({ showViewKycModal : true })}>View KYC</span>
            {renderAcceptAndReject()}
        </span>
      </React.Fragment>
    );
  };

  const renderRejectedStatus = (title) => {
    return (
      <div className={classes.rejectionStatus}>
        <div className={classes.connectionStatus}>Connection Status</div>
        <div>
          <Tooltip title={inquiryData.rejectionRemark}>
            <Icon type="exclamation-circle" theme="filled" className={classes.rejectionRemark} />
          </Tooltip>
          <span className={classes.rejected}>{title}</span>
        </div>
      </div>
    );
  };

  const getConnectionStatus = () => {
    const { connectionStatus } = inquiryData
    switch (connectionStatus) {
      case OrganisationOnboardingStatus.PENDING_APPROVAL: return renderPendingStatus();
      case OrganisationOnboardingStatus.REJECTED_BY_ME: return renderRejectedStatus('Rejected');
      case OrganisationOnboardingStatus.BLACKLISTED_BY_ME: return renderRejectedStatus('Blacklisted');
      default: return;
    }
  }

  const renderConnectionConfirmationModal = () => {
    const {
      onboardingId,
      shipperName,
      statusTransition,
      organisationLogoUrl,
      lastStatusUpdatedAt,
    } = confirmConnectionDetails;
    return (
      <ConfirmConnectionRequestModal
        visible={showConfirmConnectionModal}
        onboardingId={onboardingId}
        organisationName={shipperName}
        organisationLogoUrl={organisationLogoUrl}
        statusTransition={statusTransition}
        lastStatusUpdatedAt={lastStatusUpdatedAt}
        onClose={(success) => {
          setState({
            showConfirmConnectionModal: false,
          });
          success && handleReload();
        }}
      />
    );
  }

  const renderConnectionStatusHeader = () => {
    return (
      <div className={classes.connectionRequest}>
        {getConnectionStatus()}
        {showViewKycModal && renderViewKYCModal()}
        {showConfirmConnectionModal && renderConnectionConfirmationModal()}
      </div>
    )
  }

  return (
    renderConnectionStatusHeader()
  );
};

export default withStyles(styles)(ConnectionStatusInquiry);
